import { useEffect, useState } from "react";
import { connect } from "react-redux";
import Template from "./Template";
import { User } from "../model/user";
import { RootState } from "../model";
import { Practice } from "../model/practice";
import { useActions } from "../redux/actions";
import * as usersActions from "../redux/actions/users";
import * as practiceActions from "../redux/actions/practice";
import { Button, Checkbox, Dropdown, Form, Grid, Icon } from "semantic-ui-react";
import OpenDentalUserResult from "./OpenDentalUserResult";
import Loading from "../Loading";

interface Props {
  auth: User;
  practice: Practice;
  loader: any;
}

interface GroupAssignment {
  group: string;
  practices: string[];
}

function OpenDentalUsers(props: Props) {
  const UsersActions = useActions(usersActions);
  const PracticeActions = useActions(practiceActions);

  const [result, setResult] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserDetails, setSelectedUserDetails] = useState([] as any[]);
  const [hidden, setHidden] = useState(false);
  const [listHidden, setListHidden] = useState(false);
  const [groupAssignments, setGroupAssignments] = useState<GroupAssignment[]>([]);
  const [availableGroups, setAvailableGroups] = useState<any[]>([]);
  const [allPracticeOptions, setAllPracticeOptions] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const { loader } = props;

  useEffect(() => {
    loadUsersAndGroups(listHidden);
    UsersActions.getOdUserGroups().then((groupList: any) => {
      setAvailableGroups(
        (groupList || []).map((item: any, idx: number) => ({
          key: idx,
          text: item.groupName.trim(),
          value: item.groupName.trim(),
        }))
      );
    });
    PracticeActions.getAllPractices().then((practices: any) => {
      setAllPracticeOptions(
        (practices || []).map((item: any) => ({
          key: item.practice.practice,
          text: item.practice.practice,
          value: item.practice.practice,
        }))
      );
    });
  }, []);

  const deriveGroupAssignments = (userDetails: any[]): GroupAssignment[] => {
    const groupMap: Record<string, string[]> = {};
    userDetails.forEach((detail: any) => {
      const group = (detail.groupName || '').trim();
      if (!group) return;
      if (!groupMap[group]) groupMap[group] = [];
      groupMap[group].push(detail.location);
    });
    return Object.entries(groupMap).map(([group, practices]) => ({ group, practices }));
  };

  const loadUsersAndGroups = (isHidden: boolean) => {
    UsersActions.loadUsersAndGroups({ IsHidden: isHidden }).then((result: any) => {
      const userMap: any = {};

      for (const location in result) {
        result[location].forEach((user: any) => {
          const normalizedUserName = user.UserName.trim().toLowerCase().replace(/\s+/g, ' ');
          if (!userMap[normalizedUserName]) {
            userMap[normalizedUserName] = {
              UserName: user.UserName.trim(),
              IsHidden: user.IsHidden,
              userDetails: [],
            };
          }
          userMap[normalizedUserName].userDetails.push({
            UserNum: user.UserNum,
            location: location,
            UserName: user.UserName.trim(),
            groupName: user.groupName,
          });
        });
      }

      const options: any = Object.keys(userMap).map((normalizedKey) => ({
        text: userMap[normalizedKey].UserName,
        value: normalizedKey,
        key: normalizedKey,
        IsHidden: userMap[normalizedKey].IsHidden,
        userDetails: userMap[normalizedKey].userDetails,
      }));

      setUserOptions(options);

      if (options.length > 0) {
        const defaultUser = options[0];
        setSelectedUserName(defaultUser.value);
        setSelectedUserDetails(defaultUser.userDetails);
        setGroupAssignments(deriveGroupAssignments(defaultUser.userDetails));
        setHidden(!!defaultUser.IsHidden);
      }
      setUsersLoading(false);
    });
  };

  const handleUserChange = (_e: any, { value }: any) => {
    setSelectedUserName(value);
    const selectedUser: any = userOptions.find((user: any) => user.value === value);
    if (selectedUser && Array.isArray(selectedUser.userDetails)) {
      setSelectedUserDetails(selectedUser.userDetails);
      setGroupAssignments(deriveGroupAssignments(selectedUser.userDetails));
      setHidden(!!selectedUser.IsHidden);
    }
  };

  const handleListHiddenUsers = (_e: any, { checked }: any) => {
    setListHidden(checked);
    loadUsersAndGroups(checked);
  };

  const handleGroupChange = (index: number, group: string) => {
    const updated = [...groupAssignments];
    updated[index] = { ...updated[index], group };
    setGroupAssignments(updated);
  };

  const handlePracticesChange = (index: number, practices: string[]) => {
    const updated = [...groupAssignments];
    updated[index] = { ...updated[index], practices };
    setGroupAssignments(updated);
  };

  const handleAddGroup = () => {
    setGroupAssignments([...groupAssignments, { group: "", practices: [] }]);
  };

  const handleRemoveGroup = (index: number) => {
    setGroupAssignments(groupAssignments.filter((_, i) => i !== index));
  };

  const handleStatusChange = (_e: any, { checked }: any) => {
    setHidden(!!checked);
  };

  // Unique practice options derived from the selected user's existing assignments
  // Practices the user already has a record at (has a UserNum)
  const existingLocations = new Set(selectedUserDetails.map((d: any) => d.location));

  const userPracticeOptions = Array.from(
    new Map(selectedUserDetails.map((d: any) => [d.location, d])).values()
  ).map((d: any) => ({ key: d.location, text: d.location, value: d.location }));

  // All practices — new ones (no UserNum) are marked with a note
  const enrichedPracticeOptions = allPracticeOptions.map((opt: any) => ({
    ...opt,
    text: existingLocations.has(opt.value) ? opt.text : `${opt.text} (new — use Create User)`,
    disabled: !existingLocations.has(opt.value),
  }));

  const handleSubmit = async () => {
    // Build location → groupNames[] map (a practice can belong to multiple groups)
    const locationGroups: Record<string, string[]> = {};
    groupAssignments.forEach(({ group, practices }) => {
      if (!group) return;
      practices.forEach((location) => {
        if (!locationGroups[location]) locationGroups[location] = [];
        if (!locationGroups[location].includes(group)) locationGroups[location].push(group);
      });
    });

    // One detail per location, carrying all group names and the existing UserNum
    const details = Object.entries(locationGroups).map(([location, groupNames]) => {
      const existing = selectedUserDetails.find((d: any) => d.location === location);
      return {
        location,
        UserName: existing?.UserName ?? selectedUserName,
        UserNum: existing?.UserNum,
        groupNames,
      };
    });

    const result = await UsersActions.updateOpenDentalUser({
      userName: selectedUserName,
      details,
      IsHidden: hidden,
    });
    setResult(result);
  };

  // Groups already used — to prevent duplicate group rows
  const usedGroups = groupAssignments.map((a) => a.group).filter(Boolean);

  return (
    <Template activeLink="update-user">
      <div className="ui card">
        <div className="content">
          <h2 className="float-left mr10">Open Dental Users</h2>
        </div>
      </div>

      <Grid>
        <Grid.Row>
          <Grid.Column computer={10} tablet={16} mobile={16}>
            <div className="ui card">
              <div className="content">
                <Form onSubmit={handleSubmit}>
                  <Form.Field>
                    <Checkbox
                      label="List Hidden Users"
                      checked={listHidden}
                      onChange={handleListHiddenUsers}
                    />
                  </Form.Field>

                  <div className="mb15">
                    <label>User: </label>
                    <Dropdown
                      placeholder="Select User"
                      fluid={true}
                      search={true}
                      selection={true}
                      loading={usersLoading}
                      options={userOptions}
                      value={selectedUserName}
                      onChange={handleUserChange}
                    />
                  </div>

                  <div className="mb15">
                    <label>Group Assignments:</label>

                    {groupAssignments.length === 0 && (
                      <div style={{ color: "#999", marginTop: 6, marginBottom: 6, fontSize: 13 }}>
                        No group assignments. Click "Add Group" to add one.
                      </div>
                    )}

                    {groupAssignments.map((assignment, index) => {
                      // Exclude groups already used in other rows (but allow current row's group)
                      const groupOptions = availableGroups.map((g) => ({
                        ...g,
                        disabled: usedGroups.includes(g.value) && g.value !== assignment.group,
                      }));

                      return (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 8,
                            marginBottom: 10,
                            padding: "10px 12px",
                            border: "1px solid #e0e0e0",
                            borderRadius: 6,
                            background: "#fafafa",
                          }}
                        >
                          {/* Group selector */}
                          <div style={{ minWidth: 160 }}>
                            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>Group</div>
                            <Dropdown
                              placeholder="Select Group"
                              selection={true}
                              search={true}
                              options={groupOptions}
                              value={assignment.group}
                              onChange={(_, { value }) => handleGroupChange(index, value as string)}
                              style={{ minWidth: 150 }}
                            />
                          </div>

                          {/* Practices selector */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: "#888", marginBottom: 3 }}>
                              Practices ({assignment.practices.length} selected)
                            </div>
                            <Dropdown
                              placeholder="Select Practices"
                              multiple={true}
                              fluid={true}
                              search={true}
                              selection={true}
                              options={allPracticeOptions.length > 0 ? enrichedPracticeOptions : userPracticeOptions}
                              value={assignment.practices}
                              onChange={(_, { value }) =>
                                handlePracticesChange(index, value as string[])
                              }
                            />
                          </div>

                          {/* Remove button */}
                          <div style={{ paddingTop: 22 }}>
                            <Button
                              type="button"
                              icon={true}
                              size="mini"
                              color="red"
                              basic={true}
                              onClick={() => handleRemoveGroup(index)}
                              title="Remove this group assignment"
                            >
                              <Icon name="trash alternate outline" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}

                    <Button
                      type="button"
                      size="mini"
                      basic={true}
                      color="blue"
                      onClick={handleAddGroup}
                    >
                      <Icon name="plus" /> Add Group
                    </Button>
                  </div>

                  <Form.Field>
                    <Checkbox
                      label="Disable"
                      checked={hidden}
                      onChange={handleStatusChange}
                    />
                  </Form.Field>

                  <Form.Button primary={true} style={{ marginTop: 10 }}>
                    Submit
                  </Form.Button>
                </Form>
              </div>
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {result && result.length ? (
        <OpenDentalUserResult
          result={result}
          message={"User has been updated successfully!"}
          handleClose={() => setResult([])}
        />
      ) : (
        ""
      )}
      {loader && <Loading />}
    </Template>
  );
}

function mapStateToProps(state: RootState) {
  return {
    auth: state.auth,
    practice: state.practice,
    loader: state.app.loader,
  };
}

export default connect(mapStateToProps)(OpenDentalUsers);
